import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { getPatients, createPatient } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/dashboard/ui";
import { Plus, Search, ChevronRight, X } from "lucide-react";

export default function Patients() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", primary_diagnosis: "", program: "", tags: "" });
  const load = () => getPatients().then(setList).catch(() => toast.error("Failed to load patients"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    try {
      await createPatient({ ...form, age: parseInt(form.age) || null, tags: form.tags ? form.tags.split(",").map((t) => t.trim()) : [] });
      toast.success("Patient added"); setOpen(false); setForm({ name: "", age: "", primary_diagnosis: "", program: "", tags: "" }); load();
    } catch { toast.error("Could not add patient"); }
  };
  const filtered = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fade-in">
      <PageHeader title="Patients" subtitle={`${list.length} children in your care`} action={<button data-testid="add-patient" onClick={() => setOpen(true)} className="btn-primary px-5 py-2.5 font-semibold inline-flex items-center gap-2"><Plus size={18} /> Add Patient</button>} />
      <div className="flex items-center gap-2.5 rounded-2xl bg-white border border-slate-100 px-4 py-3 max-w-md mb-5 card-soft"><Search size={18} className="text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients…" className="flex-1 bg-transparent outline-none text-[14.5px]" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {filtered.map((p) => (
          <Link key={p.id} to={`/patients/${p.id}`} data-testid={`patient-${p.id}`} className="card-soft card-hover p-5 flex gap-4">
            <img src={p.photo} alt={p.name} className="h-20 w-20 rounded-2xl object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2"><h4 className="font-display text-[17px] font-bold text-[#10233f]">{p.name}</h4><StatusChip status={p.status} /></div>
              <p className="text-[13px] text-slate-500">Age {p.age} · {p.program}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">{(p.tags || []).slice(0, 3).map((t) => <span key={t} className="chip bg-[#eef5ff] text-[#1a5fd0]">{t}</span>)}</div>
            </div>
            <ChevronRight size={18} className="text-slate-300 self-center" />
          </Link>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="card-soft w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-[20px] font-bold text-[#10233f]">Add Patient</h3><button onClick={() => setOpen(false)}><X size={20} className="text-slate-400" /></button></div>
            <div className="space-y-3">
              {[["name", "Full name", "text"], ["age", "Age", "number"], ["primary_diagnosis", "Primary diagnosis", "text"], ["program", "Program", "text"], ["tags", "Tags (comma separated)", "text"]].map(([k, label, type]) => (
                <div key={k}><label className="text-sm font-semibold text-[#10233f]">{label}</label><input data-testid={`patient-${k}`} type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1.5 w-full card-soft px-4 py-2.5 outline-none" style={{ borderRadius: 12 }} /></div>
              ))}
            </div>
            <button data-testid="save-patient" onClick={save} className="btn-primary w-full mt-5 py-3 font-semibold">Save Patient</button>
          </div>
        </div>
      )}
    </div>
  );
}
